import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Two-step email OTP auth.
 *   step="email" → collect email (+ optional name), POST /auth/email/start
 *   step="code"  → collect 6-digit code, POST /auth/email/verify
 * Google OAuth path is preserved verbatim.
 */
export default function AuthModal() {
  const { modalOpen, closeModal, startEmailOtp, verifyEmailOtp,
          loginGoogle, onAuthed } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (!modalOpen) {
      setStep("email"); setEmail(""); setName(""); setCode("");
      setResendCooldown(0); setBusy(false);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === "code" && codeRef.current) codeRef.current.focus();
  }, [step]);

  const startOtp = async (isResend = false) => {
    if (!email) return;
    setBusy(true);
    try {
      await startEmailOtp(email, name);
      setStep("code");
      setResendCooldown(30);
      toast.success(isResend ? "Code re-sent. Check your email."
                             : "Check your email for the 6-digit code.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not send code.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your email."); return;
    }
    setBusy(true);
    try {
      await verifyEmailOtp(email, code);
      toast.success("You're in.");
      onAuthed();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg
                                          : "Invalid or expired verification code.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={modalOpen} onOpenChange={(v) => !v && closeModal()}>
      <DialogContent className="bg-[color:var(--oh-bg)] border oh-hairline rounded-none max-w-md p-0"
                     data-testid="auth-modal">
        <div className="px-8 sm:px-10 py-10">
          <DialogTitle className="oh-label" data-testid="auth-title">VOIHU</DialogTitle>

          {step === "email" ? (
            <>
              <h2 className="font-serif-editorial text-4xl sm:text-5xl leading-[1.05] mt-4 text-[color:var(--oh-ink)]">
                Add your voice.
              </h2>
              <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
                Sign in only when you need to. Reading a MESSAGE never requires an account.
              </p>

              <form onSubmit={(e) => { e.preventDefault(); startOtp(false); }}
                    className="mt-8 space-y-5">
                <div>
                  <label className="oh-label">Email</label>
                  <input type="email" required value={email}
                         onChange={(e) => setEmail(e.target.value)}
                         placeholder="you@world.earth"
                         className="oh-input" data-testid="auth-email-input" />
                </div>
                <div>
                  <label className="oh-label">Name (optional)</label>
                  <input type="text" value={name}
                         onChange={(e) => setName(e.target.value)}
                         placeholder="How you want to appear"
                         className="oh-input" data-testid="auth-name-input" />
                </div>
                <button type="submit" disabled={busy}
                        className="btn-primary w-full" data-testid="auth-email-submit">
                  {busy ? "Sending code…" : "Continue"}
                </button>
              </form>

              <div className="my-6 flex items-center gap-4">
                <div className="flex-1 h-px bg-[color:var(--oh-border)]" />
                <span className="oh-label">or</span>
                <div className="flex-1 h-px bg-[color:var(--oh-border)]" />
              </div>

              <button onClick={loginGoogle} className="btn-ghost w-full"
                      data-testid="auth-google">
                Continue with Google
              </button>
            </>
          ) : (
            <>
              <h2 className="font-serif-editorial text-4xl sm:text-5xl leading-[1.05] mt-4 text-[color:var(--oh-ink)]"
                  data-testid="auth-otp-title">
                Check your email
              </h2>
              <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
                We sent a 6-digit verification code to{" "}
                <span className="text-[color:var(--oh-ink)]">{email}</span>.
              </p>

              <form onSubmit={verify} className="mt-8 space-y-5">
                <div>
                  <label className="oh-label">6-digit code</label>
                  <input ref={codeRef} type="text" inputMode="numeric"
                         autoComplete="one-time-code" maxLength={6}
                         value={code}
                         onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         placeholder="123456"
                         className="oh-input tracking-[.35em] text-center text-lg"
                         data-testid="auth-otp-input" />
                </div>
                <button type="submit" disabled={busy || code.length !== 6}
                        className="btn-primary w-full" data-testid="auth-otp-verify">
                  {busy ? "Verifying…" : "Verify"}
                </button>
              </form>

              <div className="mt-6 flex items-center justify-between text-xs text-[color:var(--oh-ink-3)]">
                <button type="button"
                        onClick={() => setStep("email")}
                        className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
                        data-testid="auth-otp-change-email">
                  Change email
                </button>
                <button type="button"
                        disabled={busy || resendCooldown > 0}
                        onClick={() => startOtp(true)}
                        className="underline underline-offset-2 hover:text-[color:var(--oh-ink)] disabled:opacity-50 disabled:no-underline"
                        data-testid="auth-otp-resend">
                  {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
                </button>
              </div>
            </>
          )}

          <p className="mt-6 text-xs text-[color:var(--oh-ink-3)] leading-relaxed">
            By continuing you agree that VOIHU may email you about your MESSAGES and VOICES.{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer"
               className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
               data-testid="auth-terms-link">Terms of Service</a>
            {" · "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer"
               className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
               data-testid="auth-privacy-link">Privacy Policy</a>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
